import { useEffect, useId, useRef, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { Flag } from "@/components/bits";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  countryInfo,
  matchesCountrySearch,
  WORLD_COUNTRIES,
} from "@/lib/countries";
import { cn } from "@/lib/utils";

export function CountrySelect({
  id,
  value,
  onValueChange,
  testId,
}: {
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const options = WORLD_COUNTRIES.filter((country) =>
    matchesCountrySearch(country, search),
  );

  useEffect(() => {
    if (open)
      document
        .getElementById(`${listId}-${active}`)
        ?.scrollIntoView({ block: "nearest" });
  }, [open, active, listId, search]);

  function select(code: string) {
    if (code !== value) onValueChange(code);
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setSearch("");
        setActive(0);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          data-testid={testId}
          className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="flex min-w-0 items-center gap-2">
            <Flag code={value} />
            <span className="truncate">
              {countryInfo(value)?.name ?? value}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[max(18rem,var(--radix-popover-trigger-width))] max-w-[calc(100vw-2rem)] p-0"
        aria-label="Choisir un pays"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          input.current?.focus();
        }}
      >
        <div className="relative border-b p-2">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-5 top-4 h-4 w-4 text-muted-foreground"
          />
          <Input
            ref={input}
            type="search"
            role="combobox"
            aria-label="Rechercher un pays"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              options[active] ? `${listId}-${active}` : undefined
            }
            placeholder="Pays, code ou indicatif…"
            className="pl-9"
            data-testid="input-country-search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setActive(0);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const step = event.key === "ArrowDown" ? 1 : -1;
                setActive((current) =>
                  options.length
                    ? (current + step + options.length) % options.length
                    : 0,
                );
              } else if (event.key === "Enter") {
                event.preventDefault();
                if (options[active]) select(options[active].code);
              }
            }}
          />
        </div>
        <div
          id={listId}
          role="listbox"
          aria-label="Pays"
          className="max-h-[min(18rem,var(--radix-popover-content-available-height))] overflow-y-auto p-1"
        >
          {options.map((country, index) => (
            <button
              key={country.code}
              id={`${listId}-${index}`}
              type="button"
              role="option"
              aria-selected={country.code === value}
              tabIndex={-1}
              className={cn(
                "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm",
                active === index && "bg-accent text-accent-foreground",
              )}
              onMouseEnter={() => setActive(index)}
              onClick={() => select(country.code)}
            >
              <Flag code={country.code} />
              <span className="min-w-0 flex-1">{country.name}</span>
              <span className="text-xs text-muted-foreground">
                {country.dial || "—"}
              </span>
              {country.code === value && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </button>
          ))}
          {!options.length && (
            <p role="status" className="p-3 text-sm text-muted-foreground">
              Aucun pays trouvé.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
